/**
 * Settings → Help: the in-app help center, pushed like the native
 * HelpCenterView → HelpSectionView → HelpArticleView. Renders the cached
 * corpus instantly (local-first, works offline) and refreshes it in the
 * background. Content comes from the worker's GET /public/help via
 * helpContentService.
 *
 * Pages: `help` (hub + search), `help:<section>`, `help:<section>/<article>`.
 */

import type { HelpArticle, HelpSection } from "@caliu/types";
import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  getCachedHelp,
  refreshHelp,
  type CachedHelp,
} from "../../lib/services/helpContentService";
import { useUIStore, type SettingsPage } from "../../store/ui";
import { cn } from "@/lib/utils";
import { SharedNoteContent } from "../SharedNoteContent";
import { Button } from "@/components/ui/Button";
import { Icon, type IconId } from "@/components/ui/Icon";
import { SettingsEmpty, SettingsRow, SettingsSection } from "./parts";

/** Semantic icon keys from the help content. Swift maps the same keys to its
 * Espurna symbols. */
const SECTION_ICONS: Record<string, IconId> = {
  rocket: "rocket",
  pencil: "edit",
  tag: "tag",
  search: "search",
  refresh: "sync",
  share: "share",
  bell: "bell",
  arrows: "arrow-left-right",
  palette: "palette",
  user: "user",
  code: "code",
  layers: "layers",
};

/** The section and article a `help:` page points at, if the corpus has them. */
export function resolveHelpPage(
  page: string,
  help: CachedHelp | null,
): { section?: HelpSection; article?: HelpArticle } {
  const [sectionSlug, articleSlug] = page.slice("help:".length).split("/");
  const section = help?.payload.sections.find((s) => s.slug === sectionSlug);
  const article = articleSlug
    ? section?.articles.find((a) => a.slug === articleSlug)
    : undefined;
  return { section, article };
}

/** The title of a `help:` page: Help Center › Section › Article, ancestors clickable. */
export function HelpBreadcrumb({ page }: { page: `help:${string}` }) {
  const { t } = useTranslation();
  const openSettings = useUIStore((s) => s.openSettings);
  const { section, article } = resolveHelpPage(page, getCachedHelp());
  const crumbs: { label: string; page: SettingsPage }[] = [{ label: t("help.title"), page: "help" }];
  if (section) crumbs.push({ label: section.title, page: `help:${section.slug}` });
  if (section && article) crumbs.push({ label: article.title, page });
  return (
    <span className="flex min-w-0 items-center gap-1.5">
      {crumbs.map((c, i) => {
        const last = i === crumbs.length - 1;
        return (
          // Ancestors give way first, so a narrow title still shows the article.
          <span key={c.page} className={cn("flex min-w-0 items-center gap-1.5", last ? "shrink" : "shrink-[3]")}>
            {last ? (
              <span className="truncate">{c.label}</span>
            ) : (
              <>
                <Button
                  variant="ghost"
                  onClick={() => openSettings(c.page)}
                  className="h-auto min-w-0 shrink overflow-hidden border-0 p-0 font-[inherit] text-muted hover:bg-transparent hover:text-accent"
                >
                  <span className="truncate">{c.label}</span>
                </Button>
                <Icon name="chevron-right" size={12} className="shrink-0 text-muted" />
              </>
            )}
          </span>
        );
      })}
    </span>
  );
}

export function HelpSettings({ page }: { page: "help" | `help:${string}` }) {
  const { t } = useTranslation();
  const openSettings = useUIStore((s) => s.openSettings);
  const [help, setHelp] = useState<CachedHelp | null>(getCachedHelp);
  const [query, setQuery] = useState("");
  // Nothing cached and the refresh brought nothing (offline or failed).
  const [failed, setFailed] = useState(false);
  const [attempt, setAttempt] = useState(0);

  useEffect(() => {
    // Background refresh; re-read the cache if it changed. Best-effort: the
    // cached corpus (if any) is already on screen.
    void refreshHelp()
      .catch(() => {})
      .then(() => {
        const cached = getCachedHelp();
        setHelp(cached);
        setFailed(cached === null);
      });
  }, [attempt]);

  const sections = useMemo(() => help?.payload.sections ?? [], [help]);

  const trimmed = query.trim().toLowerCase();
  const searchHits = useMemo(() => {
    if (!trimmed) return [];
    return sections.flatMap((section) =>
      section.articles
        .filter(
          (article) =>
            article.title.toLowerCase().includes(trimmed) ||
            article.summary.toLowerCase().includes(trimmed),
        )
        .map((article) => ({ section, article })),
    );
  }, [sections, trimmed]);

  const openArticle = (section: HelpSection, article: HelpArticle) =>
    openSettings(`help:${section.slug}/${article.slug}`);

  if (!help) {
    if (!failed) return <SettingsEmpty loading />;
    // The same on every client: the offline icon in a soft circle, the
    // message, a real "Try again".
    return (
      <div className="flex flex-1 flex-col items-center justify-center gap-3.5 px-6 py-12 text-center">
        <span className="flex h-14 w-14 items-center justify-center rounded-full bg-sidebar text-ink-secondary">
          <Icon name="offline" size={22} />
        </span>
        <p className="max-w-[320px] text-[15px] font-medium text-ink">{t("help.offline")}</p>
        <Button
          variant="outline"
          className="mt-0.5"
          onClick={() => {
            setFailed(false);
            setAttempt((n) => n + 1);
          }}
        >
          {t("common.tryAgain")}
        </Button>
      </div>
    );
  }

  if (page !== "help") {
    const { section, article } = resolveHelpPage(page, help);
    if (article) {
      return (
        <article>
          <h1 className="mb-3 text-3xl font-bold font-headings">{article.title}</h1>
          <SharedNoteContent content={article.body} mentionMap={{}} />
        </article>
      );
    }
    if (section) {
      return (
        <SettingsSection>
          {section.articles.map((a) => (
            <SettingsRow
              key={a.slug}
              label={a.title}
              subtitle={a.summary}
              onClick={() => openArticle(section, a)}
            />
          ))}
        </SettingsSection>
      );
    }
    // The corpus changed under a stale page (language switch): nothing to show.
    return <SettingsEmpty title={t("help.noResults")} />;
  }

  return (
    <>
      <div className="relative">
        <Icon
          name="search"
          size={14}
          className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-muted"
        />
        <input
          type="search"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder={t("help.searchPlaceholder")}
          className="w-full rounded-full border border-border bg-canvas py-2 pl-8 pr-3 text-sm text-ink placeholder:text-placeholder focus:border-accent focus:outline-none"
        />
      </div>

      {trimmed ? (
        searchHits.length > 0 ? (
          <SettingsSection>
            {searchHits.map(({ section, article }) => (
              <SettingsRow
                key={`${section.slug}/${article.slug}`}
                label={article.title}
                subtitle={article.summary}
                onClick={() => openArticle(section, article)}
              />
            ))}
          </SettingsSection>
        ) : (
          <SettingsEmpty title={t("help.noResults")} />
        )
      ) : (
        <SettingsSection>
          {sections.map((section) => (
            <SettingsRow
              key={section.slug}
              icon={SECTION_ICONS[section.icon] ?? "book-open"}
              label={section.title}
              subtitle={section.description}
              onClick={() => openSettings(`help:${section.slug}`)}
            />
          ))}
        </SettingsSection>
      )}
    </>
  );
}
